import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@monksflow/monks-ui';
import { ExternalLinkIcon } from 'lucide-react';

import { InlineCitation } from '../../../../components';
import { useCitationForDataset } from '../../lib/useCitationForDataset';
import { useIsDownloadRoute } from '../../../../utils/useIsDownloadRoute';

import { StatistaChartFacts } from './StatistaChartFacts';
import type { StatistaChartCardProps } from './StatistaChartCard.types';

/** The largest crop offered, for a row that carries no full-size render. */
const widestTeaserSrc = (teasers: { width: number; src: string }[]): string | undefined =>
  [...teasers].sort((a, b) => a.width - b.width).at(-1)?.src;

export const StatistaChartCard = ({ dataset, citedIn }: StatistaChartCardProps) => {
  const { t } = useTranslation('analysis');
  const citation = useCitationForDataset(citedIn, dataset.deepLink);
  const exporting = useIsDownloadRoute();
  // The src that failed, not a boolean: this card is reused in place when the competitor
  // selector swaps datasets, and a boolean would hide the next chart too.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const { title, deepLink, imageUrl, teaserImageUrls, smallBaseFlag, respondentBase } = dataset;

  // No `srcSet`: a width descriptor drops `src` from the candidate set entirely, so offering
  // the crops would mean the full-size render is never the image the browser chooses. The
  // crops are different croppings of the chart rather than one image at several sizes, so
  // they are a fallback, not a responsive set.
  const src = imageUrl ?? widestTeaserSrc(teaserImageUrls);
  const showImage = Boolean(src) && src !== failedSrc;

  return (
    <div className="flex min-w-0 flex-col gap-[18px] rounded-lg border border-border-light px-8 py-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h3 className="text-heading-sm font-medium text-foreground">{title}</h3>
        <div className="flex flex-wrap items-center gap-2">
          {smallBaseFlag && (
            <Badge variant="warning" size="sm">
              {respondentBase === null
                ? t('analysisResult.statistaCharts.smallBaseUnknown')
                : t('analysisResult.statistaCharts.smallBase', { count: respondentBase })}
            </Badge>
          )}
          {citation ? (
            <InlineCitation link={citation} />
          ) : (
            deepLink && (
              <a
                href={deepLink}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-body-sm text-foreground-alt underline underline-offset-2"
              >
                {t('analysisResult.statistaCharts.viewOnStatista')}
                <ExternalLinkIcon className="size-3.5" aria-hidden />
              </a>
            )
          )}
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 items-stretch gap-8 md:grid-cols-[minmax(0,2.4fr)_minmax(120px,1fr)]">
        {showImage ? (
          <img
            src={src}
            alt={t('analysisResult.statistaCharts.chartAlt', { title })}
            // The PDF capture waits on `img.complete`, which a lazy image below the
            // fold never reaches: it does not start loading, so the wait times out and
            // the chart is missing from the export.
            loading={exporting ? 'eager' : 'lazy'}
            className="mx-auto h-auto max-h-80 w-auto max-w-full self-start rounded-md bg-surface object-contain"
            onError={() => setFailedSrc(src ?? null)}
          />
        ) : (
          <div className="flex min-h-32 flex-1 items-center justify-center rounded-md bg-surface px-4 py-8">
            <span className="text-body-md text-muted-foreground">
              {t('analysisResult.statistaCharts.chartUnavailable')}
            </span>
          </div>
        )}
        <StatistaChartFacts dataset={dataset} />
      </div>
    </div>
  );
};
